// Waypoint API Client
// Handles sending waypoints to backend via HTTP API

import { API_URL } from './config';

/**
 * Get authentication headers with Firebase ID token
 * @param {Object} user - Firebase user object
 * @returns {Promise<Object>} Headers object with Authorization token
 */
const getAuthHeaders = async (user) => {
  if (!user) {
    throw new Error('User not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Send a single waypoint to the backend
 * @param {Object} user - Firebase user object
 * @param {Object} waypoint - Single waypoint object with position and rotation
 * @returns {Promise<Object>} Response data from backend
 *
 * @example
 * const waypoint = {
 *   position: { x: 1.0, y: 2.0, z: 3.0 },
 *   rotation: { x: 0, y: 0, z: 0, w: 1 }
 * };
 * const result = await sendSingleWaypoint(user, waypoint);
 */
export const sendSingleWaypoint = async (user, waypoint) => {
  try {
    const headers = await getAuthHeaders(user);

    const response = await fetch(`${API_URL}/waypoints`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ waypoints: [waypoint] })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return {
      success: true,
      data,
      status: response.status
    };

  } catch (error) {
    console.error('❌ Failed to send single waypoint:', error);
    return {
      success: false,
      error: error.message,
      status: error.status || 500
    };
  }
};

/**
 * Send multiple waypoints to the backend (batch)
 * @param {Object} user - Firebase user object
 * @param {Array} waypoints - Array of waypoint objects with position and rotation
 * @param {number} gndElevation - Optional ground elevation in meters (default: 300.6)
 * @returns {Promise<Object>} Response data from backend
 *
 * @example
 * const waypoints = [
 *   { position: { x: 1.0, y: 2.0, z: 3.0 }, rotation: { x: 0, y: 0, z: 0, w: 1 } },
 *   { position: { x: 4.0, y: 5.0, z: 6.0 }, rotation: { x: 0, y: 0.707, z: 0, w: 0.707 } }
 * ];
 * const result = await sendBatchWaypoints(user, waypoints, 300.6);
 */
export const sendBatchWaypoints = async (user, waypoints, gndElevation = null) => {
  if (!waypoints || waypoints.length === 0) {
    return {
      success: false,
      error: 'No waypoints provided',
      status: 400
    };
  }

  try {
    const headers = await getAuthHeaders(user);

    // Build request payload
    const payload = { waypoints };

    // Add gnd_elevation only if specified (let backend use default if not provided)
    if (gndElevation !== null && gndElevation !== undefined) {
      payload.gnd_elevation = gndElevation;
    }

    const response = await fetch(`${API_URL}/waypoints`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return {
      success: true,
      data,
      status: response.status,
      count: waypoints.length
    };

  } catch (error) {
    console.error('❌ Failed to send batch waypoints:', error);
    return {
      success: false,
      error: error.message,
      status: error.status || 500
    };
  }
};

/**
 * Format waypoint for API (helper function)
 * Ensures correct number precision and format
 * @param {Object} waypoint - Raw waypoint object
 * @returns {Object} Formatted waypoint
 */
export const formatWaypoint = (waypoint) => {
  return {
    position: {
      x: Number(waypoint.position.x.toFixed(5)),
      y: Number(waypoint.position.y.toFixed(5)),
      z: Number(waypoint.position.z.toFixed(5))
    },
    rotation: {
      x: Number(waypoint.rotation.x.toFixed(5)),
      y: Number(waypoint.rotation.y.toFixed(5)),
      z: Number(waypoint.rotation.z.toFixed(5)),
      w: Number(waypoint.rotation.w.toFixed(5))
    }
  };
};
